import { getLdsHotelFromPackage } from "./ldsDealHelpers";
import { get_depret_from_result } from "./LDSHelpers";
import { formatDiscoveryStayDates } from "./vacationPackagesDiscovery/formatDiscoveryStayDates";
import {
  computeVacationPackageCheckoutPricingNis,
  resolveVacationPackageAgentFinalPriceFromAgentFlow,
  vacationPackageRowToSelectedResultStub,
} from "./vacationPackageCheckoutPricing";
import { resolvePublicPackageDisplayPrice } from "./formatCurrency";
import { buildPublicPackageCardUsdCurrencyDisplay } from "./vacationPackageCurrencyDisplay";

function resolvePassengerCountFromRoomInfo(roomInfo) {
  const rooms = roomInfo?.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) return 2;
  let total = 0;
  for (const room of rooms) {
    total +=
      Math.max(0, Number(room.adults ?? room.AdultsCount ?? room.adultsCount ?? 0) || 0) +
      Math.max(
        0,
        Number(room.kids ?? room.KidsCount ?? room.kidsCount ?? room.children ?? 0) || 0
      );
  }
  return Math.max(1, total || 2);
}

function ldsPackageRoot(dealRow) {
  return dealRow?.fullDeal && typeof dealRow.fullDeal === "object" ? dealRow.fullDeal : dealRow;
}

/** אותם קצות טיסה/מלון כמו mapLdsDealToDiscoveryCard — ללא שכבת תצוגה נפרת */
export function resolvePackageStayEndpoints(dealRow) {
  const full = ldsPackageRoot(dealRow) || {};
  const hotel = getLdsHotelFromPackage(dealRow);
  const payloadDates = dealRow?.__searchPayloadDates;
  const dep =
    payloadDates?.checkIn ||
    full.depDate ||
    full.dep_date ||
    dealRow?.depDate ||
    dealRow?.dep_date ||
    hotel?.checkInDate ||
    hotel?.checkIn;
  const ret =
    payloadDates?.checkOut ||
    full.retDate ||
    full.ret_date ||
    dealRow?.retDate ||
    dealRow?.ret_date ||
    hotel?.checkOutDate ||
    hotel?.checkOut;
  if (dep && ret) return { dep, ret };
  const fromFlights = get_depret_from_result(full?.linkedFlights ? full : dealRow);
  return {
    dep: dep || fromFlights?.depDate || null,
    ret: ret || fromFlights?.retDate || null,
  };
}

/**
 * SSOT לכרטיסי רשימה ציבוריים: מחיר רק מ־computeVacationPackageCheckoutPricingNis;
 * תאריכים/לילות מפורמטים אחיד דרך formatDiscoveryStayDates (מקור נתוני העסקה, לא חישוב מחיר מקביל).
 *
 * @returns {{
 *   packageId: string|null,
 *   dateRangeLabel: string,
 *   nightsLabel: string|null,
 *   finalPriceIls: number|null,
 *   finalPriceUsd: number|null,
 *   currencyDisplay: {
 *     primaryUsdLine: string|null,
 *     perPersonUnit: string|null,
 *     totalUsdLine: string|null,
 *     perPersonHint: string|null,
 *   },
 *   ssotValidation: boolean,
 *   pricingBlockedReason: string|null,
 * }}
 */
export function buildPublicVacationPackageListCardDisplayMeta({
  dealRow,
  calculateLDSDealFareForRoomsComposition,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  usdToIlsRate,
  pricingSourceTag = "unknown",
  surface = "unknown",
  roomInfo = null,
}) {
  const stub = vacationPackageRowToSelectedResultStub(dealRow);
  const full = ldsPackageRoot(dealRow) || {};
  const packageId =
    full.dealId ??
    full.ldsDealId ??
    stub?.dealId ??
    stub?.ldsDealId ??
    null;

  const { dep, ret } = resolvePackageStayEndpoints(dealRow);
  const { rangeLabel, stayLabel } = formatDiscoveryStayDates(dep, ret);

  const checkout = computeVacationPackageCheckoutPricingNis({
    selectedResult: stub,
    roomsAndPaxInfo: [],
    ourPrice: null,
    ldsSupplement: null,
    calculateLDSDealFareForRoomsComposition,
    publicSitePricingSettings,
    publicPackagePriceSnapshot: null,
    pricingSourceTag,
    agentHotelPricing,
  });

  const finalPriceIls =
    checkout?.totalNis != null && Number.isFinite(Number(checkout.totalNis))
      ? Number(checkout.totalNis)
      : null;

  let agentFlow = null;
  try {
    agentFlow = resolveVacationPackageAgentFinalPriceFromAgentFlow({
      selectedResult: stub,
      calculateLDSDealFareForRoomsComposition,
    });
  } catch {
    agentFlow = null;
  }

  const ssotValidation =
    agentFlow?.forbiddenSourceUsed === false &&
    finalPriceIls != null &&
    finalPriceIls > 0 &&
    !checkout?.pricingBlockedReason;

  const currencyPack =
    finalPriceIls != null
      ? resolvePublicPackageDisplayPrice({
          priceIlsRaw: finalPriceIls,
          usdToIlsRate,
        })
      : null;

  const finalPriceUsd =
    currencyPack?.priceUsdRaw != null && Number.isFinite(Number(currencyPack.priceUsdRaw))
      ? Number(currencyPack.priceUsdRaw)
      : null;

  const passengerCount = resolvePassengerCountFromRoomInfo(roomInfo);
  const currencyDisplay =
    finalPriceIls != null
      ? buildPublicPackageCardUsdCurrencyDisplay(
          finalPriceIls,
          usdToIlsRate,
          passengerCount,
          roomInfo
        )
      : {
          primaryUsdLine: null,
          perPersonUnit: null,
          totalUsdLine: null,
          perPersonHint: null,
        };

  const meta = {
    packageId: packageId != null ? String(packageId) : null,
    dateRangeLabel: rangeLabel,
    nightsLabel: stayLabel,
    finalPriceIls,
    finalPriceUsd,
    currencyDisplay,
    ssotValidation,
    pricingBlockedReason: checkout?.pricingBlockedReason ?? null,
  };

  try {
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("DEBUG_PUBLIC_CARD_PRICING") === "true"
    ) {
      console.log("[PUBLIC_CARD_DISPLAY_META]", {
        surface,
        packageId: meta.packageId,
        dateRangeLabel: meta.dateRangeLabel,
        nightsLabel: meta.nightsLabel,
        finalPriceIls: meta.finalPriceIls,
        finalPriceUsd: meta.finalPriceUsd,
        ssotValidation: meta.ssotValidation,
      });
    }
  } catch {
    /* ignore */
  }

  return meta;
}
